import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DropdownItem, DropdownMenu, Icon } from "semantic-ui-react";

import { Dropdown, InterventionModal } from "components";
import "components/AdvisedCriticalHistory/components/AdvisedCriticalHistoryTable/components/AdvisedCriticalHistoryActions/components/AdvisedCriticalHistoryActionsSelector/AdvisedCriticalHistoryActionsSelector.scss";
import { useShowPriceInVat } from "hooks";
import { useTyreTitleAndSeasons } from "modules/AppointmentDetails/components/Checks/components/Check/components/QuestionResults/hooks/useTyreTitleAndSeasons";
import { useAppointmentDealer, useAppointmentLocation, useCanConvertToIntervention } from "modules/AppointmentDetails/hooks";
import { convertQuestionResultToIntervention } from "modules/AppointmentDetails/utils";
import { canWriteBackCustomerData } from "utils/appointmentUtils";

import type { Appointment, QuestionResult } from "models";
import type { AddInterventionPayload } from "modules/AppointmentDetails/hooks";

type AdvisedCriticalHistoryActionsSelectorProps = {
  appointment?: Appointment;
  questionResult: QuestionResult;
  onInterventionAddSuccess: () => void;
};

export const AdvisedCriticalHistoryActionsSelector = ({ questionResult, appointment, onInterventionAddSuccess }: AdvisedCriticalHistoryActionsSelectorProps) => {
  const { t } = useTranslation();

  const [showConvertToInterventionModal, setShowConvertToInterventionModal] = useState<AddInterventionPayload | null>(null);

  const isQuestionResultItemConverted = appointment?.interventions?.some(intervention => intervention.question_result_id === questionResult.id) ?? false;

  const appointmentDealer = useAppointmentDealer(appointment);
  const appointmentLocation = useAppointmentLocation(appointment);
  const showPriceInVat = useShowPriceInVat();
  const { tyreTitle, seasonOptions } = useTyreTitleAndSeasons(questionResult.tyre_position);
  const canConvertToIntervention = useCanConvertToIntervention(appointmentLocation, isQuestionResultItemConverted);

  if (!appointment) return null;

  const handleConvertToIntervention = () =>
    setShowConvertToInterventionModal(
      convertQuestionResultToIntervention({
        title: questionResult.tyre ? tyreTitle : questionResult.title,
        questionResult,
        appointment,
        seasonOptions,
        location: appointmentLocation,
        showPriceInVat
      })
    );

  return (
    <>
      <div className="AdvisedCriticalHistoryActionsSelector">
        <Dropdown
          disabled={!canConvertToIntervention}
          className="action-dropdown"
          floating
          icon={null}
          direction="left"
          trigger={<Icon className={isQuestionResultItemConverted ? "link horizontal" : "plus grey"} />}
        >
          <DropdownMenu className="dropdown-menu">
            {canConvertToIntervention && (
              <DropdownItem onClick={handleConvertToIntervention}>
                <Icon className="screwdriver wrench" />
                {t("v8_convert_to_intervention", "Convert to Intervention")}
              </DropdownItem>
            )}
          </DropdownMenu>
        </Dropdown>
      </div>

      <InterventionModal
        appointmentId={appointment.id}
        carId={appointment.car_id}
        dealer={appointmentDealer}
        location={appointmentLocation}
        intervention={showConvertToInterventionModal}
        open={showConvertToInterventionModal !== null}
        onInterventionCreated={onInterventionAddSuccess}
        onClose={() => setShowConvertToInterventionModal(null)}
        canWriteBackCustomerData={canWriteBackCustomerData(appointmentLocation)}
      />
    </>
  );
};
